import { skipToken } from "@reduxjs/toolkit/query";
import type { Chapter } from "@repo/app-types/entities/course";
import type {
  PlaybookId,
  PlaybookNode,
  PlaybookTree,
} from "@repo/app-types/entities/playbook";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import {
  IconCircleX,
  IconClipboardList,
  IconHelpCircle,
} from "@tabler/icons-react";
import type React from "react";
import { VisuallyHidden } from "react-aria";
import {
  Checkbox,
  Heading,
  Tree,
  TreeItem,
  TreeItemContent,
  type Key,
} from "react-aria-components";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { serializeAssessmentTreeItemKey } from "../../components/recordingAssessmentEditor/assessmentTreeHelpers.js";
import { useLearningJourneyPlaybookId } from "./learningJourneyPlaybookHooks.js";

interface ChapterSettingsProps {
  chapter: Chapter;
  playbookId: PlaybookId | null;
  onChange: (
    chapterId: string,
    patchChapter: (chapter: Chapter) => Chapter,
  ) => void;
}

export const ChapterSettings: React.FC<ChapterSettingsProps> = ({
  chapter,
  playbookId: configuredPlaybookId,
  onChange,
}) => {
  const { playbookId, isLoading: isLoadingDefault } =
    useLearningJourneyPlaybookId(configuredPlaybookId);
  const { data: playbook, isLoading: isLoadingPlaybook } = usePlaybookByIdQuery(
    playbookId ?? skipToken,
  );
  const isLoading = isLoadingDefault || isLoadingPlaybook;
  // const { expandedKeys, onExpandedChange } = useAssessmentTreeExpandAndCollapse(
  //   playbook?.assessment_tree ?? { nodes: [] },
  // );

  return (
    <div className="space-y-6 p-3">
      <Input
        label="Topic"
        description="This will be visible to participants to help them understand what they will learn."
        placeholder="Example: Pricing"
        value={chapter.topic ?? ""}
        onChange={(event) => {
          const newTopic = event.target.value;
          onChange(chapter.id, (prevChapter) => ({
            ...prevChapter,
            topic: newTopic,
          }));
        }}
        maxLength={100}
      />

      <section className="space-y-6">
        <Heading
          level={4}
          className="mt-12 -mb-2 text-sm font-semibold text-muted-foreground"
        >
          Grading
        </Heading>

        <div className="flex flex-col items-start space-y-1">
          <Label size={Size.SMALL}>Playbook preparedness</Label>

          <div className="flex items-center gap-2">
            {isLoading ? (
              <>
                <IconHelpCircle className="size-4 text-gray-600" />

                <div className="h-5 w-32 py-1">
                  <div className="h-full w-32 animate-pulse rounded-full bg-gray-200" />
                  <VisuallyHidden>Loading...</VisuallyHidden>
                </div>
              </>
            ) : playbook ? (
              <>
                <IconClipboardList className="size-4 text-purple-600" />

                <div className="text-sm">{playbook.name}</div>
              </>
            ) : (
              <>
                <IconCircleX className="size-4 text-gray-600" />

                <div className="text-sm">No playbook configured</div>
              </>
            )}
          </div>

          <div className="text-sm text-gray-600">
            Participants will be assessed for their ability to succeed in
            executing relevant parts of your playbook.
          </div>
        </div>

        {/* Left here since we might need it if playbook adherence grading needs more guidance. */}
        {/* <div className="space-y-1">
          <Label size={Size.SMALL}>Applicable parts of the playbook</Label>

          {playbook ? (
            renderAssessmentTree(
              playbook.assessment_tree,
              expandedKeys,
              onExpandedChange,
            )
          ) : (
            <DefaultSkeleton />
          )}
        </div> */}
      </section>
    </div>
  );
};

function renderAssessmentTree(
  tree: PlaybookTree,
  expandedKeys: readonly string[],
  onExpandedChange: (keys: Set<Key>) => void,
) {
  return (
    <Tree
      className="space-y-1 text-sm"
      selectionMode="multiple"
      expandedKeys={expandedKeys}
      onExpandedChange={onExpandedChange}
    >
      {renderAssessmentTreeNodes(tree.nodes)}
    </Tree>
  );
}

function renderAssessmentTreeNodes(nodes: readonly PlaybookNode[]) {
  return nodes.map((node) => (
    <TreeItem
      key={node.id}
      id={serializeAssessmentTreeItemKey(node)}
      textValue={node.name}
      className="data-[level=2]:pl-5 data-[level=3]:pl-10 data-[level=4]:pl-15 selected:font-semibold"
    >
      <TreeItemContent>
        <Checkbox
          slot="selection"
          className="flex cursor-pointer items-center gap-2"
        >
          <div className="h-4 w-4 rounded-sm border border-border-strong text-indigo-600 focus:border-indigo-500 focus:ring-indigo-500">
            &nbsp;
          </div>

          {node.name}
        </Checkbox>
      </TreeItemContent>

      {node.type === "section" ? renderAssessmentTreeNodes(node.nodes) : null}
    </TreeItem>
  ));
}
